import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Mail, Sparkles, Lock, ShieldCheck } from "lucide-react";
import { env } from "../lib/env";
import { setSession, isAdminCredential, ADMIN_EMAIL } from "../lib/session";

/* ──────────────────────────────────────────────────────────────────
   Login gate — email-only for users; admin email reveals password.
   Submission posts to /api/login (Cloudflare Pages Function) so every
   visit is tracked.
   ────────────────────────────────────────────────────────────────── */

export default function Login(): JSX.Element {
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function isValidEmail(e: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  const isAdminAttempt = email.trim().toLowerCase() === ADMIN_EMAIL;

  async function onSubmit(ev: React.FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    const e = email.trim().toLowerCase();
    if (!isValidEmail(e)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (isAdminAttempt && !isAdminCredential(e, password)) {
      setError("Admin password is incorrect.");
      return;
    }
    setSubmitting(true);
    try {
      await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: e,
          ts: new Date().toISOString(),
          referrer: typeof document !== "undefined" ? document.referrer : "",
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          isAdmin: isAdminAttempt,
        }),
      }).catch(() => { /* tracking failure shouldn't block the user */ });
      setSession({
        email: e,
        signedInAt: new Date().toISOString(),
        ...(isAdminAttempt ? { isAdmin: true } : {}),
      });
      // Same-origin cookies so the /api/* Pages Functions can identify
      // the user (for Connect flow, etc.). Strict same-site, 24h.
      // Production swaps to Cloudflare Access JWT.
      if (typeof document !== "undefined") {
        const enc = encodeURIComponent(e);
        document.cookie = `foyrmind.session.email=${enc}; Path=/; Max-Age=86400; SameSite=Strict; Secure`;
        if (isAdminAttempt) {
          document.cookie = "foyrmind.admin=1; Path=/; Max-Age=86400; SameSite=Strict; Secure";
        }
      }
      const dest = (loc.state as { from?: string } | null)?.from ?? (isAdminAttempt ? "/admin/prompts" : "/");
      nav(dest, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-[calc(100vh-128px)] grid place-items-center bg-cream relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-foyr/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-sea/10 blur-3xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-[440px] max-w-[92vw] panel-hero p-8 sm:p-10"
      >
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo-lockup.svg" alt={env.VITE_BRAND_NAME} className="h-7 w-auto" />
        </div>

        <div className="eyebrow">FoyrMind cockpit</div>
        <h1 className="display-2 mt-1.5 text-ink-950">Welcome.</h1>
        <p className="mt-2 text-[13px] text-ink-700 leading-snug">
          Enter your work email to open the reasoning cockpit. No password — we use this only
          to remember you across sessions and to know who's exploring.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-ink-500">Email</span>
            <div className="mt-1 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
                autoFocus
                autoComplete="email"
                inputMode="email"
                required
                className="w-full bg-cream border border-border rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foyr/30"
              />
            </div>
          </label>

          <AnimatePresence initial={false}>
            {isAdminAttempt ? (
              <motion.label
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="block overflow-hidden"
              >
                <span className="text-[11px] uppercase tracking-wider text-ink-500 inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-foyr" /> Admin password
                </span>
                <div className="mt-1 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-500" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="w-full bg-cream border border-border rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foyr/30"
                  />
                </div>
              </motion.label>
            ) : null}
          </AnimatePresence>

          {error ? <div className="text-[12px] text-foyr">{error}</div> : null}

          <button
            type="submit"
            disabled={submitting || !email || (isAdminAttempt && !password)}
            className="btn btn-foyr w-full !py-2.5 !text-sm"
          >
            {submitting ? "Opening cockpit…" : isAdminAttempt ? "Sign in as admin" : "Continue"}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="text-[11px] text-ink-500 leading-snug pt-2">
            By continuing you agree that we may store your email to remember you and to follow up
            about the demo. We never sell or share it.
          </div>
        </form>

        <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-[11px] text-ink-500">
          <a
            href={env.VITE_BRAND_SITE_URL}
            target="_blank"
            rel="noreferrer"
            className="hover:text-ink-950 inline-flex items-center gap-1"
          >
            <Sparkles className="w-3 h-3 text-foyr" /> hypergraphmind.com
          </a>
          <span>Prove your reasoning.</span>
        </div>
      </motion.div>
    </div>
  );
}
